/**
 * <PaymentBreakdownPanel>
 * ------------------------------------------------------------------
 * A dedicated, at-a-glance breakdown of the Sales Report's payment
 * mix — Cash, Card, Bank Transfer, Cheque, Others (row), then a
 * clearly-separated Outstanding Due row, closing with Total Paid
 * and Grand Total.
 *
 * Data source is the `summary` block of `GET /api/reports`, which
 * already aggregates split payments correctly via
 * `_payment_amounts()` in the backend — so this component is a
 * pure presentation layer.
 */
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Banknote, CreditCard, Building2, FileCheck, Clock, Wallet, Layers } from 'lucide-react';
import { formatCurrency as fmtCurrency } from '../../lib/formatters';
import { useTranslation } from 'react-i18next';

const METHOD_ROWS = [
    { key: 'total_cash',          label: 'cash',   icon: Banknote,  color: 'text-[#2A9D8F]', bar: '#2A9D8F' },
    { key: 'total_card',          label: 'card',   icon: CreditCard, color: 'text-[#264653]', bar: '#264653' },
    { key: 'total_bank_transfer', label: 'bank',   icon: Building2, color: 'text-[#E9C46A]', bar: '#E9C46A' },
    { key: 'total_cheque',        label: 'cheque', icon: FileCheck, color: 'text-[#8B5CF6]', bar: '#7C6BAD' },
    { key: 'total_others',        label: 'others', icon: Layers,    color: 'text-[#6B7280]', bar: '#9CA3AF' },
];

/** Inline horizontal bar — matches the PDF's `.bar-track > .bar-fill`. */
const PbBar = ({ value, denom, color }) => {
    const pct = Math.min(100, ((Number(value || 0)) / (denom || 1)) * 100);
    return (
        <div className="h-1.5 w-full rounded-full bg-[#F1EAE6] overflow-hidden">
            <div
                className="h-full rounded-full"
                style={{ width: `${pct.toFixed(1)}%`, background: color }}
            />
        </div>
    );
};

const PaymentBreakdownPanel = ({ summary }) => {
    const { t, i18n } = useTranslation(['entities', 'common']);
    if (!summary) return null;

    const money = (v) => fmtCurrency(v || 0, i18n.language);
    const totalPaid = Number(summary.total_paid || 0);
    const totalDue = Number(summary.total_due || 0);
    const grand = Number(summary.grand_total || 0);
    // Denominator for %: prefer paid+due (i.e. full invoice value) so
    // percentages sum to 100% including the outstanding slice. Falls
    // back to `grand` in case of rounding.
    const denom = (totalPaid + totalDue) || grand || 1;

    const pct = (v) => `${((Number(v || 0) / denom) * 100).toFixed(1)}%`;
    const hasAnyMethod = METHOD_ROWS.some((r) => (summary[r.key] || 0) > 0) || totalDue > 0;

    return (
        <Card className="border-0 shadow-sm no-break" data-testid="payment-breakdown-panel">
            <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                    <Wallet size={18} className="text-[#C08478]" />
                    {t('entities:reports.payment_breakdown.title', { defaultValue: 'Payment Breakdown' })}
                </CardTitle>
            </CardHeader>
            <CardContent>
                {!hasAnyMethod ? (
                    <p className="text-sm text-gray-400 py-6 text-center" data-testid="pbp-empty">
                        {t('entities:reports.payment_breakdown.empty', { defaultValue: 'No payments recorded for this period.' })}
                    </p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-[11px] uppercase tracking-wider text-gray-400 border-b border-gray-100">
                                    <th className="pb-2 font-medium">{t('entities:reports.payment_breakdown.method', { defaultValue: 'Method' })}</th>
                                    <th className="pb-2 font-medium text-right">{t('entities:reports.payment_breakdown.amount', { defaultValue: 'Amount' })}</th>
                                    <th className="pb-2 font-medium w-[32%]" aria-hidden="true"></th>
                                    <th className="pb-2 font-medium text-right w-24">%</th>
                                </tr>
                            </thead>
                            <tbody>
                                {METHOD_ROWS.map((row) => {
                                    const amount = Number(summary[row.key] || 0);
                                    if (amount === 0) return null;
                                    const Icon = row.icon;
                                    return (
                                        <tr key={row.key} className="border-b border-gray-50 last:border-b-0" data-testid={`pbp-row-${row.label}`}>
                                            <td className="py-2.5">
                                                <div className="flex items-center gap-2.5">
                                                    <Icon size={16} className={row.color} />
                                                    <span className="text-[#1A1A1A]">
                                                        {t(`entities:reports.methods.${row.label}`, { defaultValue: row.label })}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="py-2.5 text-right font-mono font-semibold text-[#1A1A1A] euro-amount">
                                                {money(amount)}
                                            </td>
                                            <td className="py-2.5 px-3 align-middle">
                                                <PbBar value={amount} denom={denom} color={row.bar} />
                                            </td>
                                            <td className="py-2.5 text-right text-gray-500 font-mono text-xs">
                                                {pct(amount)}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                            <tfoot>
                                {/* Total Paid — sum of every settled method */}
                                <tr className="border-t border-gray-200">
                                    <td className="pt-3 pb-2 text-[#1A1A1A] font-medium">
                                        {t('entities:reports.payment_breakdown.total_paid', { defaultValue: 'Total Paid' })}
                                    </td>
                                    <td className="pt-3 pb-2 text-right font-mono font-bold text-[#1A1A1A] euro-amount" data-testid="pbp-total-paid">
                                        {money(totalPaid)}
                                    </td>
                                    <td className="pt-3 pb-2 px-3 align-middle">
                                        <PbBar value={totalPaid} denom={denom} color="#2A9D8F" />
                                    </td>
                                    <td className="pt-3 pb-2 text-right text-gray-500 font-mono text-xs">
                                        {pct(totalPaid)}
                                    </td>
                                </tr>
                                {/* Outstanding Due — highlighted */}
                                {totalDue > 0 && (
                                    <tr data-testid="pbp-row-due">
                                        <td className="py-2">
                                            <div className="flex items-center gap-2.5">
                                                <Clock size={16} className="text-[#E76F51]" />
                                                <span className="text-[#E76F51] font-medium">
                                                    {t('entities:reports.payment_breakdown.outstanding_due', { defaultValue: 'Outstanding Due' })}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="py-2 text-right font-mono font-bold text-[#E76F51] euro-amount" data-testid="pbp-total-due">
                                            {money(totalDue)}
                                        </td>
                                        <td className="py-2 px-3 align-middle">
                                            <PbBar value={totalDue} denom={denom} color="#E76F51" />
                                        </td>
                                        <td className="py-2 text-right text-[#E76F51] font-mono text-xs">
                                            {pct(totalDue)}
                                        </td>
                                    </tr>
                                )}
                                {/* Grand Total — invoiced value (paid + due) */}
                                <tr className="border-t-2 border-[#1A1A1A]/10 bg-[#FAFAFA]">
                                    <td className="pt-3 pb-1 text-[#1A1A1A] font-semibold">
                                        {t('entities:reports.payment_breakdown.grand_total', { defaultValue: 'Grand Total (invoiced)' })}
                                    </td>
                                    <td className="pt-3 pb-1 text-right font-mono font-bold text-[#1A1A1A] text-base euro-amount" data-testid="pbp-grand-total">
                                        {money(grand)}
                                    </td>
                                    <td className="pt-3 pb-1 px-3 align-middle">
                                        <PbBar value={grand} denom={denom} color="#1A1A1A" />
                                    </td>
                                    <td className="pt-3 pb-1 text-right text-gray-400 font-mono text-xs">
                                        100.0%
                                    </td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                )}
            </CardContent>
        </Card>
    );
};

export default PaymentBreakdownPanel;
